import { useEffect } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import type { ComponentType } from "react";
import type { ModuleManifest } from "@praesto/contracts";
import {
  LogOut,
  Moon,
  Monitor,
  PanelLeftClose,
  Settings,
  Sun,
} from "lucide-react";
import { useCore } from "./core";
import { useCommandRegistry, type Command } from "@praesto/core";
import { setTheme } from "./theme";
import { toggleSubSidebar } from "./sidebar-state";

/**
 * Registers built-in commands: theme/sidebar/logout/settings always,
 * plus one navigation command per module and per sub-route. Re-runs
 * when manifests change so navigation commands stay in sync.
 *
 * Single useEffect that builds the command list and registers each
 * one — avoids the React rule "hooks must be called in same order".
 */
export function useBuiltInCommands(
  manifests: ReadonlyArray<ModuleManifest>,
): void {
  const navigate = useNavigate();
  const core = useCore();
  const { tenantSlug } = useParams({ strict: false }) as {
    tenantSlug?: string;
  };
  const { register } = useCommandRegistry();

  useEffect(() => {
    if (!tenantSlug) return;
    const cast = <T,>(c: T) =>
      c as unknown as ComponentType<{ size?: number }>;
    const cmds: Command[] = [
      {
        id: "theme.light",
        label: "Tema chiaro",
        icon: cast(Sun),
        category: "action",
        action: () => setTheme("light"),
      },
      {
        id: "theme.dark",
        label: "Tema scuro",
        icon: cast(Moon),
        category: "action",
        action: () => setTheme("dark"),
      },
      {
        id: "theme.system",
        label: "Tema sistema",
        icon: cast(Monitor),
        category: "action",
        action: () => setTheme("system"),
      },
      {
        id: "ui.toggleSidebar",
        label: "Riduci / espandi sidebar",
        icon: cast(PanelLeftClose),
        category: "action",
        shortcut: "⌘\\",
        action: () => toggleSubSidebar(),
      },
      {
        id: "auth.logout",
        label: "Esci",
        icon: cast(LogOut),
        category: "action",
        action: () => core.auth.signOut(),
      },
      {
        id: "nav.settings",
        label: "Impostazioni",
        icon: cast(Settings),
        category: "navigation",
        action: () => navigate({ to: `/${tenantSlug}/settings` as never }),
      },
    ];

    for (const m of manifests) {
      const Icon = m.icon as unknown as ComponentType<{ size?: number }>;
      cmds.push({
        id: `nav.${m.id}`,
        label: core.i18n.t(`${m.id}.title`),
        icon: Icon,
        category: "navigation",
        action: () => navigate({ to: `/${tenantSlug}/${m.id}` as never }),
      });
      for (const sub of m.subRoutes ?? []) {
        const SubIcon = sub.icon as unknown as ComponentType<{ size?: number }>;
        cmds.push({
          id: `nav.${m.id}.${sub.path}`,
          label: `${core.i18n.t(`${m.id}.title`)} / ${core.i18n.t(sub.labelKey)}`,
          icon: SubIcon,
          category: "navigation",
          ...(sub.requires ? { requires: sub.requires } : {}),
          action: () =>
            navigate({
              to: `/${tenantSlug}/${m.id}/${sub.path}` as never,
            }),
        });
      }
    }

    const unregisters = cmds.map((c) => register(c));
    return () => {
      for (const u of unregisters) u();
    };
  }, [tenantSlug, manifests, register, navigate, core]);
}
